import React, { useEffect, useRef, useState } from 'react';
import WebUploader from 'webUploader';
import { get, youpaiPost } from '@chat/utils';
import produce from 'immer';
import _ from 'lodash';
import { UploadVideo, WU_FILE_STATUS } from '@chat/components/src/UploadVideo/type';
import { useDispatch } from 'react-redux';
import { pushVideoToTempList, setVideoMessageUuid } from '@/redux/im/action';
import { ImState } from '@/typings/reducer';
import { IM_LOCAL_VIDEO_FILE } from '@/typings/global';

interface IReturn {
    fileList: any[];
    upload: () => void;
    open: (file: UploadVideo) => Promise<void>;
    remove: (file: UploadVideo) => void;
    cancel: (file: UploadVideo) => void;
    reUpload: () => void;
}
interface IProps {
    onChange?: ({ fileList }: { fileList: any[] }) => void;
    onException?: (type: string, msg: string) => void;
    onOpen?: (file: UploadVideo) => void;
    onSuccess?: (file: UploadVideo) => void;
    onStatusChange?: ({ file, status }: { file: UploadVideo; status: string }) => void;
    onTimeout?: (now: string) => void;
    action: string;
    actionStart: string;
    actionEnd: string;
    actionUpdate?: string;
    extData?: Record<string, any>;
    init?: boolean;
    parallel?: boolean;
    value: any[];
    accept?: string;
    auth?: any;
    initFile: File;
    STATUS: ImState['STATUS'];
}
const TIME_OUT = 60 * 1000;
const useUploadChunk = ({
    initFile,
    auth,
    value = [],
    action = '',
    actionStart = '',
    actionEnd = '',
    actionUpdate = '',
    extData = {},
    parallel = false,
    onChange = () => { },
    onException = () => { },
    onOpen = () => { },
    onSuccess = () => { },
    onStatusChange = () => { },
    onTimeout = () => { },
    accept = '.mp4 ,.avi, .flv ,.asf ,.mov ,.mpeg ,.wmv ,.rm ,.rmvb, .mpg, .3gp, .m4v, .mkv',
    STATUS
}: IProps): IReturn => {
    const dispatch = useDispatch();
    const uploader = useRef(null);
    const [fileList, setFileList] = useState([]);
    const timeoutThrottleMapRef = useRef(new Map<string, (now: string) => void>());
    const statusChanged = ({ file, status }): void => {
        console.debug('statusChanged', status);
        onStatusChange({ file, status });
        file.status = status;
        switch (status) {
            case 'inited': // 初始状态
            case 'error': // 上传出错，可重试
                // setFileList((prevFileList) => prevFileList.filter((item) => item.id !== file.id));
                break;
            case 'invalid': // 文件不合格，不能重试上传。会自动从队列中移除
                return;
            case 'cancelled': // 文件被移除
                // setFileList((prevFileList) => prevFileList.filter((item) => item.id !== file.id));
                break;
            case 'queued': // 已经进入队列, 等待上传
            case 'progress': // 上传中
            case 'interrupt': // 上传中断，可续传
            case 'complete': // 上传完成
                break;
            default:
                break;
        }
        setFileList((prevFileList) => {
            // 更新外部数据
            const newFileList = _.cloneDeep(prevFileList);
            const fileIndex = _.findIndex(newFileList, { id: file.id });
            if (newFileList[fileIndex]) newFileList[fileIndex].status = status;
            onChange?.({ fileList: newFileList });
            return newFileList;
        });
    };

    const getTimeoutThrottler = (id: string) => {
        if (!timeoutThrottleMapRef.current.has(id)) {
            timeoutThrottleMapRef.current.set(
                id,
                _.throttle((now: string) => onTimeout?.(now), 1000, {
                    leading: true,
                    trailing: false
                })
            );
        }
        return timeoutThrottleMapRef.current.get(id)!;
    };

    const uploadStart = async (file: UploadVideo): Promise<any> => {
        const res = await youpaiPost(
            actionStart,
            {
                name: file.name,
                size: file.size,
                md5: file.md5,
                app: 'chat',
                quick: 'false',
                ...extData
            },
            { headers: auth }
        );
        const NOW = dayjs().format('YYYY-MM-DD HH:mm:ss');
        const uuid = res.data.d.session_id;
        file.uuid = uuid;
        file.viedoTime = NOW;
        getTimeoutThrottler(file.id)(NOW);
        dispatch(pushVideoToTempList(file.uuid));
        if (initFile) {
            dispatch(setVideoMessageUuid(initFile as IM_LOCAL_VIDEO_FILE, uuid));
        }
        setFileList((prevFileList) =>
            produce(prevFileList, (draft) => {
                try {
                    const fileIndex = _.findIndex(draft, { id: file.id });
                    draft[fileIndex].uuid = uuid;
                    draft[fileIndex].viedoTime = NOW;
                } catch (error) { }
            })
        );
    };

    const uploadEnd = async (file: UploadVideo): Promise<any> => {
        const res = await youpaiPost(
            actionEnd,
            {
                'SESSION-ID': file.uuid,
                'FILE-MD5': file.md5,
                'FILE-TOTAL': file.size,
                ...extData
            },
            { headers: auth }
        );
        return res;
    };

    const md5File = (file: UploadVideo): Promise<any> => {
        return new Promise((resolve) => {
            uploader.current
                .md5File(file)
                .progress((percentage) => {
                    console.debug(`分片进度:${percentage.toFixed(2) * 100}%`);
                })
                .then((val) => {
                    file.md5 = val;
                    console.debug('md5 result:', val);
                    resolve(val);
                });
        });
    };

    // 时间点1：在文件发送之前request，此时还没有分片（如果配置了分片的话），可以用来做文件整体md5验证
    const beforeSendFile = (file: UploadVideo) => {
        const deferred = WebUploader.Deferred();
        md5File(file)
            .then(() => uploadStart(file))
            .then(() => {
                deferred.resolve();
            })
            .catch((error) => {
                onException('error', error?.data?.e);
                deferred.reject();
            });
        return deferred.promise();
    };

    // 时间点2：如果有分块上传，则每个分块上传之前调用此函数，判断分块存不存在
    // eslint-disable-next-line prettier/prettier
    const beforeSend = (block) => { };

    // 时间点3：在所有分片都上传完毕后，且没有错误后request，用来做分片验证
    const afterSendFile = (file: UploadVideo) => {
        const deferred = WebUploader.Deferred();
        if (file.status === 'cancelled') {
            return deferred.reject();
        }
        uploadEnd(file)
            .then(() => {
                deferred.resolve();
            })
            .catch((error) => {
                onException('error', error?.data?.e);
                deferred.reject();
            });
        return deferred.promise();
    };

    const remove = (file: UploadVideo) => {
        // if (file.id) {
        //     uploader.current.removeFile(file.id, true);
        // }
        // onChange({ fileList: [] });
        setFileList((prevFileList) => {
            const filterData = prevFileList.filter((item) => item.id !== file.id);
            onChange?.({ fileList: filterData });
            return filterData;
        });
    };

    const cancel = (file: UploadVideo) => {
        const NOW = dayjs().format('YYYY-MM-DD HH:mm:ss');
        getTimeoutThrottler(file.id)(NOW);
        uploader.current?.cancelFile(file?.id);
    };

    const reUpload = () => {
        uploader.current.addFiles(initFile);
        uploader.current.upload();
    };

    // 终止上传函数 - 专门处理 code=等严重错误
    const terminateUpload = (file: UploadVideo, errorMessage: string) => {
        console.error('终止上传:', file.name, errorMessage);

        // 立即停止所有上传
        uploader.current?.stop(true);
        uploader.current?.cancelFile(file.id);

        // 从队列中移除文件
        uploader.current?.removeFile(file.id, true);

        // 调用错误处理回调
        onException('error', errorMessage);

        // 设置文件状态为错误并更新列表
        file.status = 'error';
        setFileList((prevFileList) =>
            produce(prevFileList, (draft) => {
                const fileIndex = _.findIndex(draft, { id: file.id });
                if (draft[fileIndex]) {
                    draft[fileIndex].status = 'error';
                }
            })
        );
    };

    const updateVideo = async (file: UploadVideo): Promise<any> => {
        if (!actionUpdate) {
            return Promise.reject(new Error('暂不支持视频预览功能'));
        }
        const res = await get(actionUpdate, { uuid: file.uuid, ...extData });
        const f = res.data.d;
        const url = f?.urls?.url;
        if (url) {
            // onChange({ fileList: [f] });
            setFileList((prevFileList) =>
                produce(prevFileList, (draft) => {
                    try {
                        const fileIndex = _.findIndex(draft, { uuid: file.uuid });
                        draft[fileIndex] = { ...draft[fileIndex], ..._.omit(f, 'id') };
                    } catch (error) { }
                })
            );
            return Promise.resolve(f);
        }
        return Promise.reject(new Error('视频处理中，请稍等片刻～'));
    };

    const open = async (file: UploadVideo) => {
        // const url = file?.urls?.url;
        // if (url) {
        //     onOpen(file);
        //     return;
        // }
        try {
            const f = await updateVideo(file);
            // open(f);
            onOpen(f);
        } catch (error) {
            onException('warning', error.message);
        }
    };

    const progressChanged = ({ file, percent }: { file: UploadVideo; percent: number }): void => {
        // file.percent = parseInt(`${percent * 100}`, 10);
        // console.debug(`上传进度:${file.percent}`);
        // onChange({ fileList: [file] });
        const NOW = dayjs().format('YYYY-MM-DD HH:mm:ss');
        setFileList((prevFileList) =>
            produce(prevFileList, (draft) => {
                try {
                    const fileIndex = _.findIndex(draft, { id: file.id });
                    const { viedoTime } = draft[fileIndex];
                    draft[fileIndex].percent = parseInt(`${percent * 100}`, 10);
                    if (viedoTime && dayjs(NOW).diff(viedoTime, 'milliseconds') > TIME_OUT) {
                        draft[fileIndex].viedoTime = NOW;
                        getTimeoutThrottler(file.id)(NOW);
                    }
                } catch (error) { }
            })
        );
    };

    const handleError = (type, file: UploadVideo) => {
        switch (type) {
            case 'Q_TYPE_DENIED':
            case 'F_TYPE_DENIED':
                onException('error', `视频类型不满足`);
                return;
            case 'Q_EXCEED_NUM_LIMIT':
            case 'F_EXCEED_NUM':
                onException('error', `添加的视频数量过多`);
                return;
            case 'Q_EXCEED_SIZE_LIMIT':
            case 'F_EXCEED_SIZE':
                onException('error', `添加的视频大小不符`);
                return;
            default:
                onException('error', `上传出错！请检查后重新上传！错误代码${type}`);
        }
    };

    const upload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        input.accept = accept;
        input.addEventListener('change', async (event) => {
            const _file = input.files[0];
            if (!parallel && _.some(fileList, { status: 'progress' })) {
                onException('warning', '您有文件还未上传完成，请稍后再试');
                return;
            }
            uploader.current?.addFiles(_file);
            uploader.current?.upload();
            document.body.removeChild(input);
        });
        document.body.appendChild(input);
        input.click();
    };

    const bindEvents = () => {
        // 文件入列之前
        uploader.current.on('beforeFileQueued', async (file) => { });
        // 文件入队列
        // eslint-disable-next-line prettier/prettier
        uploader.current.on('fileQueued', async (file) => {
            file.on('statuschange', (status: WU_FILE_STATUS) => statusChanged({ file, status }));
            setFileList((prevFiles) =>
                produce(prevFiles, (draft) => {
                    draft.push({
                        id: file.id,
                        name: file.name,
                        status: 'queued',
                        percent: 0,
                        originFile: file
                    });
                })
            );
        });
        // 当某个文件的分块在发送前触发，主要用来询问是否要添加附带参数，大文件在开起分片上传的前提下此事件可能会触发多次
        uploader.current.on('uploadBeforeSend', (block, data, headers) => {
            data.trans = 'multipart';
            data['SESSION-ID'] = block.file.uuid;
            data['CONTENT-RANGE'] = `${block.start}-${block.end - 1}`;
            data.MD5 = block.file.md5;
            headers.uid = auth.uid;
            headers.token = auth.token;
            headers.now = auth.now;
            Object.entries(extData).forEach(([key, val]) => {
                data[key] = val;
            });
        });
        // 文件上传过程中创建进度条实时显示。
        uploader.current.on('uploadProgress', (file, percent) => {
            progressChanged({ file, percent });
        });
        // 当文件上传成功时触发。
        // eslint-disable-next-line prettier/prettier
        uploader.current.on('uploadSuccess', (file, response) => {
            if (response && response.code && (response.code === 201 || response.code === 200)) {
                onSuccess?.(file);
                return;
            }
            terminateUpload(file, response.message || '上传被服务器拒绝，请检查文件内容');
        });
        // 当文件上传出错时触发。
        // eslint-disable-next-line prettier/prettier
        uploader.current.on('uploadError', (file, reason) => { });
        // 不管成功或者失败，文件上传完成时触发。
        uploader.current.on('uploadComplete', (file) => {
            console.debug('off statuschange');
            file.off('statuschange');
        });
        // 当validate不通过时，会以派送错误事件的形式通知调用者。
        uploader.current.on('error', handleError);
    };

    useEffect(() => {
        if (!initFile || !auth) return;

        WebUploader.Uploader.unRegister(`widgetName`);
        // 注册生命周期hooks要在实例化之前
        WebUploader.Uploader.register(
            {
                // 整个文件上传前
                'before-send-file': 'beforeSendFile',
                // 每个分片上传前
                'before-send': 'beforeSend',
                // 分片上传完毕
                'after-send-file': 'afterSendFile',
                // 标识
                name: 'widgetName'
            },
            {
                beforeSendFile,
                beforeSend,
                afterSendFile
            }
        );
        uploader.current = WebUploader.create({
            // 文件接收服务端
            server: action,
            // 选完文件后，是否自动上传。
            auto: true,
            // 开起分片上传。
            chunked: true,
            // 如果要分片，分多大一片？ 默认大小为5M.
            chunkSize: 5 * 1024 * 1024,
            // 不压缩image, 默认如果是jpeg，文件上传前会压缩一把再上传！
            resize: false,
            // 只允许选择视频文件。
            accept: {
                title: 'Videos',
                extensions: 'mp4,avi,flv,asf,mov,mpeg,wmv,rm,rmvb,mpg,3gp,m4v,mkv,webm',
                mimeTypes:
                    '.mp4 ,.avi, .flv ,.asf ,.mov ,.mpeg ,.wmv ,.rm ,.rmvb, .mpg, .3gp, .m4v, .mkv, .webm'
            },
            // 验证文件总大小是否超出限制, 超出则不允许加入队列
            fileSingleSizeLimit: 500 * 1024 * 1024,
            // 错误重试次数
            chunkRetry: false,
            // 上传并发数。允许同时最大上传进程数
            threads: 1,
            // 是否能上传同一个视频？去重， 根据文件名字、文件大小和最后修改时间来生成hash Key
            duplicate: true
        });
        bindEvents();

        setTimeout(() => {
            uploader.current?.addFiles(initFile);
            uploader.current?.upload();
        }, 1);

        return () => {
            setTimeout(() => {
                console.debug('stop');
                // 中断上传当前正在上传的文件
                uploader.current?.stop(true);
                uploader.current = null;
                // 回收注册
                WebUploader.Uploader.unRegister(`widgetName`);
            }, 0);
        };
    }, [initFile, auth]);

    useEffect(() => {
        if (!_.isEmpty(value) && !_.isEqual(value[0]?.uuid, fileList[0]?.uuid)) {
            setFileList(value);
        }
    }, [value]);

    useEffect(() => {
        if (STATUS.CLOSED && fileList[0]) {
            cancel(fileList[0]);
        }
    }, [STATUS]);

    return {
        fileList,
        upload,
        open,
        remove,
        reUpload,
        cancel
    };
};

export default useUploadChunk;
